import { FileRow } from 'baseui/file-uploader';
import { IdName, PaymentMethod } from './app';
import { AssetCore, FuelType } from './asset';
import { OperationalStateStatus } from './currentState';
import { Vehicle } from './vehicle';

export enum FleetTriggerType {
  distance = 'distance',
  days = 'days',
}

export enum FleetServiceStatus {
  next = 'next',
  required = 'required',
  expired = 'expired',
  attended = 'attended',
  cancel = 'cancel',
}

export enum FleetJobType {
  maintenance = 'maintenance',
  repair = 'repair',
}

export interface FleetMaintenanceType {
  slug: string;
  value: string;
  _id: string;
  client?: IdName;
  description: string;
}

export interface FleetMaintenanceTypeRequest extends Omit<FleetMaintenanceType, '_id' | 'client' | 'slug'> {
  clientId: string;
  subClientId: string;
}

export interface FleetMaintenanceRule extends IdName {
  description: string;
  triggerType: FleetTriggerType;
  trigger: number;
  maintenanceType: FleetMaintenanceType;
  client?: IdName;
}

export interface FleetMaintenanceRuleRequest extends Omit<FleetMaintenanceRule, '_id' | 'client' | 'maintenanceType'> {
  maintenanceTypeId: string;
  clientId: string;
  subClientId: string;
}

export interface FleetEditMaintenanceRuleRequest extends Omit<FleetMaintenanceRuleRequest, 'triggerType'> {}

export interface FleetServiceJob {
  date: number;
  type: FleetJobType;
  description?: string;
  evidences?: string[];
  cost: number;
}

export interface FleetServiceJobRequest extends FleetServiceJob {
  clientId: string;
  subClientId: string;
  assetId?: string;
  serviceId?: string;
  lastMaintenanceDate?: number;
  evidenceFiles?: FileRow[];
}

export interface FleetVehicle extends Omit<Vehicle, '_id'> {
  // TODO: check if version is not in here
  marketValue?: number;
  documents?: FleetAssetDocumentList;
}

export interface FleetOdometerState {
  GpsOdometerBase: number;
  userInputOdometer: number;
  calculatedOdometer: number;
}

export interface FleetAsset extends Omit<AssetCore, 'inJourney' | 'journeyId' | 'vehicle'> {
  lastMaintenance?: FleetServiceJob;
  lastReparation?: FleetServiceJob;
  vehicle?: FleetVehicle;
  odometerState?: FleetOdometerState;
}

export interface FleetAssetRequest {
  clientId: string;
  subClientId: string;
  color: string;
  year: number;
  brandId: string;
  subBrandId?: string;
  versionId?: string;
  licensePlate?: string;
  label: string;
  marketValue?: number;
  odometer?: number;
}

export interface FleetAssetCurrentState {
  ident: string;
  timestamp: number;
  position_latitude: number;
  position_longitude: number;
  geoReference: string;
  deviceModel: string;
}

export interface FleetService {
  _id: 'string';
  asset: FleetAsset;
  status: FleetServiceStatus;
  configuration?: IdName;
  lastMaintenanceDate?: number;
  lastMaintenanceMileage?: number;
  nextServiceDate: number;
  serviceJob?: FleetServiceJob;
  created: number;
  cs: FleetAssetCurrentState;
}

export interface FleetServicesRequest {
  clientId: string;
  subClientId: string;
  assetIds: string[];
  configurationId?: string;
  lastMaintenanceDate: number;
  lastMaintenanceMileage?: number;
}

export enum FleetAssetDocumentStatus {
  valid = 1,
  about_to_expire = 2,
  expired = 3,
}

export interface FleetAssetDocumentData {
  expirationDate: number;
  document: string;
  comments: string;
  status: FleetAssetDocumentStatus;
}

export interface FleetAssetDocumentDataRequest extends Omit<FleetAssetDocumentData, 'expirationDate' | 'status'> {
  expirationDate?: number;
  fileRow?: FileRow;
}

export interface FleetAssetDocumentList {
  [key: string]: FleetAssetDocumentData;
}

export interface FleetAssetDocumentListRequest {
  [key: string]: Omit<FleetAssetDocumentData, 'status'>;
}

export enum FleetAssetDocumentType {
  insurancePolicy = 'insurance',
  emissionsTest = 'verification',
  vehicleRegistration = 'circulation_card',
}

export interface FleetDriverPhotoDetail {
  driverId: string;
  client: IdName;
  subClient: IdName;
  assetId?: string;
  positionTs: number;
  label: string;
  ident: string;
  photo: string;
  operationalState?: {
    currentStatus: OperationalStateStatus;
    lastStatusChange: number;
    duration: number;
  };
  rekognitionFaceId: string;
  created: number;
  confidence?: number;
  similarity?: number;
  matchPhoto?: string;
}

export interface FleetAssetRefuelRequest {
  date: number;
  liters: number;
  cost: number;
  type: FuelType;
  gasStation: string;
  paymentMethod: PaymentMethod;
  reference: string;
}

export interface FleetOperational {
  startDate: number;
  endDate: number;
  ident: string;
  label: string;
  maintenances: number;
  maintenanceCosts: number;
  reparations: number;
  reparation_costs: number;
  refuels: number;
  refuel: number;
}
